import React, { useState } from 'react';
import { connect } from 'react-redux';
import templateReplace from 'es6-template-strings';
import _debounce from 'lodash/debounce';
import { arrayOf, func, string } from 'prop-types';

import AsyncSelect from 'ravenjs/lib/Select/AsyncSelect';
import Button from 'ravenjs/lib/Button';
import FormFields from 'ravenjs/lib/Form/fields';
import { get, isEmpty } from 'ravenjs/utils/lodash';
import { toast } from 'ravenjs/lib/Toaster';
import Row from 'ravenjs/lib/Row';
import Typography from 'ravenjs/lib/Typography';

import { PREPPED_ANSWERS, UNIDENTIFIED_CASES } from 'constants/tickets';
import { actions as adminActions } from 'modules/admin';
import { actions as ticketsActions } from 'modules/tickets';

const AuthorChangeForm = ({
    fetchUsers,
    transferPreppedAnswer,
    preppedAnswerIds,
    paTransferCallback,
}) => {
    const [selectedUserOption, setSelectedUserOption] = useState(null);
    const userId = get(selectedUserOption, 'value', '');
    const userName = get(selectedUserOption, 'fullName', '');
    const preppedAnswerCount = preppedAnswerIds.length;

    const fetchUsersFormatted = async (config = {}) => {
        try {
            const resp = await fetchUsers({ ...config });
            const users = get(resp, 'users', []);

            const userOptions = users.map(({ fullName, userId }) => ({
                label: `${fullName} - ${userId}`,
                value: userId,
                fullName,
            }));

            return Promise.resolve({
                userOptions,
            });
        } catch (e) {
            return Promise.reject(e);
        }
    };

    const fetchUserOptions = (searchSpec, callback) => {
        if (!searchSpec) {
            return callback && callback([]);
        }

        const params = {
            searchSpec,
            limit: 10,
            isActive: true,
            permissionKey: 'system.casecavepas.editown',
        };

        return fetchUsersFormatted(params)
            .then(response => {
                const userOptions = get(response, 'userOptions', []);
                return callback && callback(userOptions);
            })
            .catch(() => callback && callback([]));
    };

    const fetchUserOptionsDebounced = _debounce(fetchUserOptions, 500);

    const handleTransferPreppedAnswer = async () => {
        const params = {
            to_user_id: userId,
            prepped_answer_ids: preppedAnswerIds,
        };

        try {
            await transferPreppedAnswer(params);
            await paTransferCallback();
            toast.success(
                templateReplace(PREPPED_ANSWERS.DETAILS.MODALS.CHANGE_AUTHOR_MODAL.SUCCESS, {
                    userName,
                    preppedAnswerCount,
                })
            );
        } catch (e) {
            toast.error(PREPPED_ANSWERS.DETAILS.MODALS.CHANGE_AUTHOR_MODAL.ERROR);
        }
    };

    return (
        <>
            <FormFields.FormGroupTitleField title={UNIDENTIFIED_CASES.CONVERT.LABEL} />
            <AsyncSelect
                cacheOptions
                isClearable
                placeholder={UNIDENTIFIED_CASES.CONVERT.PLACEHOLDER}
                loadOptions={fetchUserOptionsDebounced}
                onChange={selectedUserOption => {
                    setSelectedUserOption(selectedUserOption);
                }}
                style={{ zIndex: 1000 }}
                useSearchIcon
                value={selectedUserOption}
                data-testid="author-change-select"
            />
            {!isEmpty(selectedUserOption) && (
                <>
                    <Row margin="0">
                        <Typography
                            type="span"
                            fontFamily="Roboto"
                            fontSize="0.875rem"
                            gutterTop="1rem"
                            gutterBottom="0"
                        >
                            {templateReplace(
                                PREPPED_ANSWERS.DETAILS.MODALS.CHANGE_AUTHOR_MODAL.CONFIRMATION,
                                { preppedAnswerCount, userName }
                            )}
                        </Typography>
                    </Row>
                    <Button
                        margin="1rem 0 0 0"
                        color="primary"
                        width="13rem"
                        onClick={handleTransferPreppedAnswer}
                    >
                        <Typography type="span" fontSize="0.813rem" gutterTop="0" gutterBottom="0">
                            {PREPPED_ANSWERS.DETAILS.MODALS.CHANGE_AUTHOR_MODAL.TITLE}
                        </Typography>
                    </Button>
                </>
            )}
        </>
    );
};

AuthorChangeForm.propTypes = {
    fetchUsers: func.isRequired,
    paTransferCallback: func.isRequired,
    preppedAnswerIds: arrayOf(string).isRequired,
    transferPreppedAnswer: func.isRequired,
};

const mapDispatchToProps = {
    fetchUsers: adminActions.fetchUsers,
    transferPreppedAnswer: ticketsActions.transferPreppedAnswer,
};

export { AuthorChangeForm as AuthorChangeFormWrapper };

export default connect(null, mapDispatchToProps)(AuthorChangeForm);
